import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Robot, Money, Megaphone, Barbell, EnvelopeSimple, Lightning, Check, X, Clock, ArrowsClockwise, Warning, ListChecks, ToggleLeft, ToggleRight, Play } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/ui/kaizen";

const AGENT_VISUALS = {
  kru_finances:  { icon: Money,         color: "#D90429", subtitle: "Surveillance comptes · doublons · kill-switch" },
  kru_marketing: { icon: Megaphone,     color: "#E76F51", subtitle: "Campagnes · countdown · planification" },
  kru_athletes:  { icon: Barbell,       color: "#2A9D8F", subtitle: "Dossiers médicaux · MTO · pesée" },
  kru_reception: { icon: EnvelopeSimple, color: "#264653", subtitle: "Tri courriels · brouillons · relances" },
};

const RISK_COLOR = { low: "#2A9D8F", medium: "#E9C46A", high: "#D90429" };
const RISK_LABEL = { low: "Auto", medium: "Notifié", high: "À confirmer" };

const fmt = (n) => `${(n || 0).toLocaleString("fr-CA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} $`;

export default function WingmanDashboard() {
  const [data, setData] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [briefing, setBriefing] = useState(null);
  const [tab, setTab] = useState("pending");  // pending | recent | all
  const [busy, setBusy] = useState(null);

  const load = async () => {
    try {
      const [d, t, b] = await Promise.all([
        api.get("/wingman/dashboard"),
        api.get("/wingman/tasks?limit=50"),
        api.get("/wingman/briefing"),
      ]);
      setData(d.data); setTasks(t.data); setBriefing(b.data);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); }, []);

  const triggerAgent = async (aid) => {
    setBusy(aid);
    try {
      const r = await api.post(`/wingman/agents/${aid}/run`);
      toast.success(`${aid.replace("kru_","Kru ")}: ${r.data.summary}`);
      await load();
    } catch (e) { toast.error("Erreur agent"); }
    finally { setBusy(null); }
  };

  const toggleAgent = async (aid) => {
    await api.post(`/wingman/agents/${aid}/toggle`);
    load();
  };

  const approveTask = async (id) => {
    await api.post(`/wingman/tasks/${id}/approve`, { notes: "" });
    toast.success("Tâche approuvée");
    load();
  };
  const rejectTask = async (id) => {
    await api.post(`/wingman/tasks/${id}/reject`, { notes: "" });
    toast.success("Tâche rejetée");
    load();
  };

  if (!data) return <div className="px-8 py-12 text-muted-foreground">Chargement…</div>;

  const filteredTasks = tab === "pending"
    ? tasks.filter(t => t.status === "awaiting_confirmation")
    : tab === "recent" ? tasks.filter(t => t.status === "auto_executed" || t.status === "info").slice(0, 30)
    : tasks;

  return (
    <div data-testid="wingman-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader title={<>Adjoint Tactique</>} subtitle={<>4 agents Kru autonomes qui surveillent, alertent et exécutent selon ta règle 80/20.
          Risque faible = auto · Risque moyen = notification · Risque élevé = confirmation requise.</>} />
      </div>

      <div className="px-8 lg:px-12 py-8 space-y-8">

        {/* Briefing */}
        {briefing && (
          <section className="hard-border p-6 bg-secondary/30" data-testid="briefing">
            <div className="ticker text-muted-foreground mb-2">{briefing.period}</div>
            <pre className="whitespace-pre-wrap font-sans text-base leading-relaxed">{briefing.narrative}</pre>
          </section>
        )}

        {/* KPI tactiques */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Link to="/app/redressement" className="hard-border p-5 hover:bg-secondary/30 transition-colors block">
            <div className="ticker text-muted-foreground mb-2">Récupération potentielle</div>
            <div className="font-mono text-3xl font-bold text-[#D90429]">{fmt(data.kpi.recovery_total)}</div>
            <div className="text-xs text-muted-foreground mt-1">Audit {data.kpi.audit_transactions.toLocaleString("fr-CA")} transactions · cible {fmt(data.kpi.audit_target)}</div>
          </Link>
          <Link to="/app/redressement" className="hard-border p-5 hover:bg-secondary/30 transition-colors block">
            <div className="ticker text-muted-foreground mb-2">Bouclier fiscal · Billy</div>
            <div className="font-mono text-3xl font-bold text-[#2A9D8F]">{fmt(data.kpi.shield_billy_total)}</div>
          </Link>
          <Link to="/app/redressement" className="hard-border p-5 hover:bg-secondary/30 transition-colors block">
            <div className="ticker text-muted-foreground mb-2">Kill-Switch · économies</div>
            <div className="font-mono text-3xl font-bold">{fmt(data.kpi.killswitch_annual)}<span className="text-sm text-muted-foreground"> /an</span></div>
            <div className="text-xs text-muted-foreground mt-1">{fmt(data.kpi.killswitch_monthly)}/mois</div>
          </Link>
        </section>

        {/* Stats 24h */}
        <section className="hard-border p-5 bg-secondary/30 flex items-center justify-between flex-wrap gap-4" data-testid="stats-24h">
          <div className="flex gap-8">
            <div>
              <div className="ticker text-muted-foreground">Cycles 24h</div>
              <div className="font-mono text-2xl font-bold">{data.stats_24h.runs}</div>
            </div>
            <div>
              <div className="ticker text-muted-foreground">Tâches créées</div>
              <div className="font-mono text-2xl font-bold">{data.stats_24h.tasks_created}</div>
            </div>
            <div>
              <div className="ticker text-muted-foreground">À confirmer</div>
              <div className="font-mono text-2xl font-bold text-[#D90429]">{data.stats_24h.pending_high_risk}</div>
            </div>
          </div>
          <div className="text-xs text-muted-foreground flex items-center gap-2">
            <ArrowsClockwise size={14}/> Cadence : 1 cycle/heure · briefings 7h & 19h
          </div>
        </section>

        {/* 4 Agents */}
        <section>
          <div className="ticker text-muted-foreground mb-4">Les 4 Super Agents</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {data.agents.map(a => {
              const v = AGENT_VISUALS[a.agent_id] || {};
              const Icon = v.icon || Robot;
              return (
                <div key={a.agent_id} className={`hard-border p-5 ${a.enabled ? "" : "opacity-50"}`} data-testid={`agent-${a.agent_id}`}>
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 hard-border flex items-center justify-center" style={{ background: v.color, color: "white" }}>
                        <Icon size={22} weight="duotone"/>
                      </div>
                      <div>
                        <div className="font-display font-black text-lg">{a.name}</div>
                        <div className="ticker text-muted-foreground">{v.subtitle}</div>
                      </div>
                    </div>
                    <button onClick={() => toggleAgent(a.agent_id)} className="hover:text-[#D90429]" title={a.enabled ? "Désactiver" : "Activer"}>
                      {a.enabled ? <ToggleRight size={26}/> : <ToggleLeft size={26}/>}
                    </button>
                  </div>
                  <div className="text-sm text-muted-foreground mb-3 min-h-[2.5em]">{a.description}</div>
                  <div className="flex items-center gap-4 text-xs mb-3 flex-wrap">
                    <span className="ticker">cycles: {a.runs_count}</span>
                    <span className="ticker">tâches: {a.tasks_executed}</span>
                    {a.tasks_pending > 0 && <span className="ticker bg-[#D90429] text-white px-2 py-0.5">{a.tasks_pending} en attente</span>}
                  </div>
                  {a.last_run_summary && (
                    <div className="text-xs text-muted-foreground border-l-2 border-border pl-3 italic mb-3">
                      Dernier cycle : {a.last_run_summary}
                    </div>
                  )}
                  {a.blocked_capabilities && a.blocked_capabilities.length > 0 && (
                    <div className="text-xs flex items-center gap-1.5 text-[#D90429] mb-3">
                      <Warning size={14}/> {a.blocked_capabilities.length} capacité(s) bloquée(s) (intégrations à connecter)
                    </div>
                  )}
                  <button onClick={() => triggerAgent(a.agent_id)} disabled={busy === a.agent_id} className="w-full bg-[#0A0A0A] hover:bg-[#D90429] text-white px-4 py-2 ticker flex items-center justify-center gap-2 transition-colors disabled:opacity-50" data-testid={`run-${a.agent_id}`}>
                    <Play size={14}/> {busy === a.agent_id ? "Exécution…" : "Lancer maintenant"}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        {/* Tâches */}
        <section>
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <div className="ticker text-muted-foreground">Tâches</div>
            {[
              { k: "pending", label: `À confirmer (${tasks.filter(t => t.status === "awaiting_confirmation").length})` },
              { k: "recent", label: "Auto-exécutées" },
              { k: "all", label: "Toutes" },
            ].map(t => (
              <button key={t.k} onClick={() => setTab(t.k)} className={`ticker px-3 py-1 hard-border ${tab === t.k ? "bg-[#D90429] text-white" : "hover:bg-secondary"}`} data-testid={`tab-${t.k}`}>
                {t.label}
              </button>
            ))}
          </div>

          {filteredTasks.length === 0 && (
            <div className="hard-border p-12 text-center" data-testid="tasks-empty">
              <ListChecks size={42} className="text-muted-foreground mx-auto mb-3"/>
              <div className="font-display font-bold text-lg">Aucune tâche</div>
              <div className="text-sm text-muted-foreground mt-1">Lance un agent pour générer des recommandations.</div>
            </div>
          )}

          {filteredTasks.length > 0 && (
            <div className="hard-border" data-testid="tasks-list">
              {filteredTasks.map(t => (
                <div key={t.id} className="p-4 border-b border-border last:border-0 flex items-start justify-between gap-4 flex-wrap" data-testid={`task-${t.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap mb-1">
                      <span className="ticker px-2 py-0.5" style={{ background: RISK_COLOR[t.risk_level], color: "white" }}>{RISK_LABEL[t.risk_level]}</span>
                      <span className="ticker text-muted-foreground">{t.agent_id.replace("kru_","Kru ")}</span>
                      {t.status === "approved" && <span className="ticker bg-[#2A9D8F] text-white px-2 py-0.5">approuvé</span>}
                      {t.status === "rejected" && <span className="ticker bg-foreground text-white px-2 py-0.5">rejeté</span>}
                      {t.status === "auto_executed" && <span className="ticker text-muted-foreground">auto</span>}
                    </div>
                    <div className="font-display font-bold">{t.title}</div>
                    <div className="text-sm text-muted-foreground mt-1">{t.description}</div>
                    {t.suggested_action && (
                      <div className="text-xs italic mt-2 flex items-center gap-1">
                        <Lightning size={12}/> {t.suggested_action}
                      </div>
                    )}
                  </div>
                  {t.status === "awaiting_confirmation" && (
                    <div className="flex items-center gap-2 shrink-0">
                      <button onClick={() => approveTask(t.id)} className="ticker bg-[#2A9D8F] hover:bg-[#228277] text-white px-3 py-1.5 flex items-center gap-1.5" data-testid={`approve-${t.id}`}>
                        <Check size={14}/> Approuver
                      </button>
                      <button onClick={() => rejectTask(t.id)} className="ticker hard-border hover:bg-foreground hover:text-white px-3 py-1.5 flex items-center gap-1.5" data-testid={`reject-${t.id}`}>
                        <X size={14}/> Rejeter
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
